import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useForm } from '../../hooks/useForm';
import { validateEmail } from '../../utils/validations';
import styles from './LoginPage.module.scss';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

interface LoginFormData {
  email: string;
  password: string;
}

const validationRules = {
  email: {
    required: true,
    custom: (value: string) => {
      if (!validateEmail(value)) {
        return 'Please enter a valid email address or Username';
      }
      return null;
    }
  },
  password: {
    required: true,
    minLength: 6
  }
};

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateForm
  } = useForm<LoginFormData>({
    email: '',
    password: ''
  }, validationRules);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const user = await login({
        email: values.email,
        password: values.password
      });
      console.error('Login:', user);
      toast.success('Login successful!');
      if(user.role === 'admin') {
        navigate('/dashboard', { replace: true });
      } else{
        navigate('/orders', { replace: true });
      }
    } catch (error: any) {
      toast.error('Invalid email or password');
      console.error('Login failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof LoginFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(field)(e.target.value);
  };

  const handleInputBlur = (field: keyof LoginFormData) => () => {
    handleBlur(field)();
  };

  return (
    <div className={styles.loginWrapper}>
      {/* Left - Login Form */}
      <div className={styles.loginLeft}>
        <div className={styles.loginCard}>
          <div className={styles.header}>
            <h1>MS LPG Agency</h1>
            <p>Sign in to your account</p>
          </div>

          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formGroup}>
              <label htmlFor="email" className={styles.label}>
                Email
              </label>
              <input
                type="text"
                id="email"
                name="login"
                value={values.email}
                onChange={handleInputChange('email')}
                onBlur={handleInputBlur('email')}
                className={`${styles.input} ${errors.email && touched.email ? styles.error : ''}`}
                placeholder="Enter your email"
                disabled={isSubmitting}
              />
              {errors.email && touched.email && (
                <span className={styles.errorText}>{errors.email}</span>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="password" className={styles.label}>
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={values.password}
                onChange={handleInputChange('password')}
                onBlur={handleInputBlur('password')}
                className={`${styles.input} ${errors.password && touched.password ? styles.error : ''}`}
                placeholder="Enter your password"
                disabled={isSubmitting}
              />
              {errors.password && touched.password && (
                <span className={styles.errorText}>{errors.password}</span>
              )}
            </div>

            <button
              type="submit"
              className={styles.submitButton}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className={styles.poweredBy}>
            <p>@2025, MS LPG Agency</p>
          </div>
          <ToastContainer position="top-right" autoClose={3000} />
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
